import { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Global } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { lightTheme, darkTheme } from './theme/index';
import { globalStyles } from './styles/globalStyles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import NavigationHeader from './components/NavigationHeader';
import OfflineIndicator from './components/OfflineIndicator';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ProtectedRoute from './components/ProtectedRoute';
import Welcome from './pages/Welcome';
import LocationDetection from './pages/LocationDetection';
import AccessSelection from './pages/AccessSelection';
import UserDashboard from './pages/UserDashboard';
import GuestDashboard from './pages/GuestDashboard';
import UserEnhancedDashboard from './pages/UserEnhancedDashboard';
import FirebaseAdminPage from './pages/FirebaseAdminPage';
import AuthPage from './pages/AuthPage';
import UserProfilePage from './pages/UserProfilePage';
import { getLanguageFromDomain, updateMetaFromDomain } from './utils/domainConfig';
import { initializeDocumentDirection } from './utils/documentDirection';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Smart Dashboard Component that chooses between Guest and User dashboard
const SmartDashboard: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  console.log('🏠 SmartDashboard: isAuthenticated =', isAuthenticated, 'user =', user?.email, 'isLoading =', isLoading);
  
  if (isLoading) {
    return <GuestDashboard />; // Show guest dashboard while loading
  }
  
  if (isAuthenticated && user) {
    console.log('🏠 SmartDashboard: Showing UserEnhancedDashboard for user:', user.email);
    return <UserEnhancedDashboard />;
  }
  
  console.log('🏠 SmartDashboard: Showing GuestDashboard for guest user');
  return <GuestDashboard />;
};

// Create a wrapper component to handle the conditional rendering
const AppContent = ({ toggleTheme }: { toggleTheme: () => void }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const showHeader = !['/welcome', '/location', '/access'].includes(location.pathname);
  const [isDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });

  // Detect language from domain and auto-switch
  useEffect(() => {
    const domainLanguage = getLanguageFromDomain();

    // Only change language if domain specifies a different one
    // and user hasn't manually changed it in this session
    const userLanguage = sessionStorage.getItem('userSelectedLanguage');

    if (!userLanguage && domainLanguage && i18n.language !== domainLanguage) {
      console.log(`🌐 Auto-switching language to: ${domainLanguage} (based on domain: ${window.location.hostname})`);
      i18n.changeLanguage(domainLanguage);
    }

    // Update page meta tags based on domain
    updateMetaFromDomain();

    // Initialize document direction for current language
    initializeDocumentDirection(i18n.language);
  }, [i18n]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <OfflineIndicator />
      <PWAInstallPrompt />
      {showHeader && <NavigationHeader isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/" element={<SmartDashboard />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/location" element={<LocationDetection />} />
          <Route path="/access" element={<AccessSelection />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/guest" element={<GuestDashboard />} />
          <Route path="/admin" element={<FirebaseAdminPage />} />
              <Route 
                path="/auth" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <AuthPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute requireAuth={true}>
                    <UserProfilePage />
                  </ProtectedRoute>
                } 
              />
        </Routes>
      </Box>

      {/* Footer with Copyright */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          textAlign: 'center',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          mt: 'auto'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Hesy Tech Corporation. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Global styles={globalStyles} />
        <AuthProvider>
          <Box
            sx={{
              transition: 'background-color 0.3s ease, color 0.3s ease',
              bgcolor: 'background.default',
              color: 'text.primary',
              minHeight: '100vh',
            }}
          >
            <Router>
              <AppContent toggleTheme={toggleTheme} />
            </Router>
          </Box>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
